import { useEffect, useState } from 'react';
import { Card } from './ui/Card';
import { Button } from './ui/Button';
import { Input } from './ui/Input';
import { Badge } from './ui/Badge';
import { Spinner } from './ui/Spinner';
import { ConfirmModal } from './ui/Modal';
import {
  getDnsRecords,
  createDnsRecords,
  deleteDnsRecord,
  type DnsRecord,
  type DnsRecordInput,
  type ManageableType,
} from '../api/dns-records';

/**
 * Staff DNS management for pointing a niftee-managed domain at an external
 * email provider (Google Workspace, Microsoft 365, or anything else).
 *
 * Deliberately generic rather than provider-templated: staff paste the values
 * the provider gave the client. Provider presets can pre-fill this later, but
 * tenant-specific values (verification tokens, DKIM keys) can only come from
 * the client's own admin console, so a template could never fill them anyway.
 *
 * Purchased domains only — a BYOD domain has no zone under our account, and the
 * server rejects it with an explanation.
 */

const TYPES: ManageableType[] = ['MX', 'TXT', 'CNAME', 'SRV'];

const emptyRow = (): DnsRecordInput => ({ type: 'MX', name: '@', content: '', priority: 10 });

export function DnsRecordsManager({ domain }: { domain: string | null }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [records, setRecords] = useState<DnsRecord[]>([]);
  const [forwardingEnabled, setForwardingEnabled] = useState<boolean | null>(null);
  const [rows, setRows] = useState<DnsRecordInput[]>([emptyRow()]);
  const [pendingConflict, setPendingConflict] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<DnsRecord | null>(null);

  const load = async () => {
    if (!domain) return;
    setLoading(true);
    setError(null);
    try {
      const data = await getDnsRecords(domain);
      setRecords(data.records);
      setForwardingEnabled(data.forwardingEnabled);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load DNS records');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [domain]);

  const updateRow = (i: number, patch: Partial<DnsRecordInput>) => {
    setRows((prev) => prev.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  };

  const submit = async (disableForwarding: boolean) => {
    if (!domain) return;
    const payload = rows.filter((r) => r.content.trim());
    if (!payload.length) {
      setError('Add at least one record.');
      return;
    }

    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const result = await createDnsRecords(domain, payload, disableForwarding);
      setSuccess(
        result.forwardingPaused
          ? `Added ${result.created.length} record(s). niftee forwarding paused — it can be re-enabled without losing addresses.`
          : `Added ${result.created.length} record(s).`
      );
      setRows([emptyRow()]);
      setPendingConflict(false);
      await load();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to add records';
      // 409 from the server means forwarding owns the MX — surface the choice
      // rather than silently breaking the customer's existing email.
      if (message.toLowerCase().includes('forwarding')) {
        setPendingConflict(true);
      }
      setError(message);
    } finally {
      setSaving(false);
    }
  };

  if (!domain) {
    return (
      <Card>
        <h3 className="text-lg font-semibold text-gray-900">Email provider DNS</h3>
        <p className="text-sm text-gray-600 mt-1">
          This site has no custom domain yet.
        </p>
      </Card>
    );
  }

  return (
    <Card>
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Email provider DNS</h3>
          <p className="text-sm text-gray-600 mt-0.5">
            Add the TXT / MX / CNAME records from Google Workspace, Microsoft 365 or another
            provider for <span className="font-medium">{domain}</span>.
          </p>
        </div>
        {forwardingEnabled === true && <Badge variant="warning">niftee forwarding active</Badge>}
        {forwardingEnabled === null && <Badge variant="default">forwarding status unknown</Badge>}
      </div>

      {error && (
        <div className="mt-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3">
          {error}
        </div>
      )}
      {success && (
        <div className="mt-3 text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg p-3">
          {success}
        </div>
      )}

      {loading ? (
        <div className="py-6 flex justify-center"><Spinner /></div>
      ) : (
        <>
          {/* Existing records */}
          <div className="mt-4">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
              Current records
            </p>
            {records.length === 0 ? (
              <p className="text-sm text-gray-500">No MX, TXT, CNAME or SRV records yet.</p>
            ) : (
              <div className="space-y-1">
                {records.map((r) => (
                  <div
                    key={r.id}
                    className="flex items-center gap-3 text-sm p-2 bg-gray-50 rounded border border-gray-200"
                  >
                    <span className="font-mono text-xs w-14 shrink-0 text-gray-500">{r.type}</span>
                    <span className="font-medium shrink-0">{r.name}</span>
                    <span className="font-mono text-xs text-gray-600 truncate flex-1">
                      {r.priority !== undefined ? `${r.priority} ` : ''}{r.content}
                    </span>
                    <button
                      onClick={() => setDeleteTarget(r)}
                      className="text-xs text-gray-400 hover:text-red-600 shrink-0"
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* New records */}
          <div className="mt-5">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
              Add records
            </p>
            <div className="space-y-2">
              {rows.map((row, i) => (
                <div key={i} className="flex gap-2 items-start">
                  <select
                    value={row.type}
                    onChange={(e) => updateRow(i, { type: e.target.value as ManageableType })}
                    className="px-2 py-2 text-sm border border-gray-300 rounded-lg w-24 shrink-0"
                  >
                    {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                  <Input
                    value={row.name}
                    onChange={(e) => updateRow(i, { name: e.target.value })}
                    placeholder="@ or subdomain"
                    className="w-40 shrink-0"
                  />
                  {(row.type === 'MX' || row.type === 'SRV') && (
                    <Input
                      type="number"
                      value={row.priority ?? 10}
                      onChange={(e) => updateRow(i, { priority: Number(e.target.value) })}
                      placeholder="Priority"
                      className="w-24 shrink-0"
                    />
                  )}
                  <Input
                    value={row.content}
                    onChange={(e) => updateRow(i, { content: e.target.value })}
                    placeholder="Value from the provider"
                    className="flex-1"
                  />
                  {rows.length > 1 && (
                    <button
                      onClick={() => setRows((p) => p.filter((_, idx) => idx !== i))}
                      className="px-2 py-2 text-gray-400 hover:text-red-600 text-sm"
                    >
                      ✕
                    </button>
                  )}
                </div>
              ))}
            </div>

            <div className="flex items-center gap-2 mt-3">
              <Button size="sm" variant="secondary" onClick={() => setRows((p) => [...p, emptyRow()])}>
                Add another
              </Button>
              <Button size="sm" variant="primary" onClick={() => void submit(false)} loading={saving}>
                Save records
              </Button>
            </div>

            <p className="text-xs text-gray-400 mt-2">
              Records are written DNS-only (never proxied). The website's own records at
              <span className="font-mono"> @ </span> and <span className="font-mono">www</span> are
              protected and can't be changed here.
            </p>
          </div>
        </>
      )}

      {/* MX conflict — pausing forwarding is reversible, so say so plainly */}
      <ConfirmModal
        isOpen={pendingConflict}
        onClose={() => setPendingConflict(false)}
        onConfirm={() => void submit(true)}
        title="Pause niftee email forwarding?"
        message={
          `${domain} currently uses niftee forwarding, which owns its MX records. ` +
          `Adding this provider's MX will stop that forwarding from working. ` +
          `Continuing pauses forwarding — the configured addresses are kept and can be ` +
          `re-enabled later if the client changes their mind.`
        }
        confirmText="Pause forwarding and add records"
        confirmVariant="primary"
      />

      <ConfirmModal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={async () => {
          if (!deleteTarget || !domain) return;
          try {
            await deleteDnsRecord(domain, deleteTarget.id);
            setDeleteTarget(null);
            await load();
          } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to remove record');
            setDeleteTarget(null);
          }
        }}
        title="Remove DNS record?"
        message={
          deleteTarget
            ? `Remove the ${deleteTarget.type} record for ${deleteTarget.name}? If the client's email depends on it, mail will stop being delivered.`
            : ''
        }
        confirmText="Remove record"
        confirmVariant="danger"
      />
    </Card>
  );
}
